import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import ICONS from "../../../../assets/Icons";
import CommonToast from "../../../../components/_utils/CommonToast";
import { env } from "../../../../env";
import axiosInstance from "../../../globalInspector/axiosInstance";

const baseUrl = env.REACT_APP_BASE_URL;

export const beneCreateAccount = createAsyncThunk(
  "createBeneficiaryAccount",
  async (values, { rejectWithValue }) => {
    const {
      bank_account,
      contact_no,
      email_id,
      gstin,
      ifsc_code,
      panNumber,
      categories,
      navigate,
      account_name,
      labels,
      recipient_email,
      reimbursement_account,
      reimbursement_user_id,
      virtual,
    } = values;

    const requestBody = {
      beneficiary_accounts: {
        bank_account_no: bank_account,
        contact_no,
        email: email_id,
        gstin,
        ifsc: ifsc_code,
        pan: panNumber,
        name: account_name,
        labels,
        category: categories?.length > 0 ? categories : null,
        recipient_email,
        reimbursement_account,
        reimbursement_user_id,
        virtual,
      },
    };

    try {
      const response = await axiosInstance.post(
        `${baseUrl}/accounts/beneficiary`,
        requestBody,
        {
          headers: {
            "Content-Type": "application/json",
            "X-Api-Key": sessionStorage.getItem("q2p_token"),
          },
        },
      );

      return response.data;
    } catch (error) {
      const responseData = error.response?.data;

      if (responseData?.errors?.user_unauthorized) {
        navigate("/feature_not_assigned", {
          state: { unavailableService: "BUAccount" },
        });
        return rejectWithValue("User unauthorized");
      }

      let errorMessage = "Something went wrong";

      if (responseData?.errors) {
        const errs = responseData.errors;

        errorMessage =
          errs?.bank_account_number?.base?.[0] ||
          errs?.bank_account_number?.[0] ||
          errs?.permanent_account_number?.[0] ||
          errs?.category?.[0] ||
          errs?.[0]?.gstin_detail?.base ||
          errs?.base?.[0] ||
          responseData?.message ||
          error.message;
      } else if (error.response?.statusText) {
        errorMessage = error.response.statusText;
      }

      return rejectWithValue({
        message: errorMessage,
        status: error.response?.status,
        errors: responseData?.errors || null,
      });
    }
  },
);

export const editBeneficiary = createAsyncThunk(
  "editBeneficiary",
  async (values, { rejectWithValue }) => {
    const {
      id,
      pan,
      contact_no,
      email,
      gstin,
      category,
      name,
      navigate,
      recipient_email,
      reimbursement_user_id,
      linked_user_id,
      type_of_account,
    } = values;

    const requestOptions = {
      headers: {
        "Content-Type": "application/json",
        "X-Api-Key": sessionStorage.getItem("q2p_token"),
      },
    };

    try {
      const response = await axiosInstance.put(
        `${baseUrl}/accounts/beneficiary/${id}/edit_other_details`,
        {
          beneficiary_accounts: {
            pan,
            email,
            contact_no,
            gstin,
            category,
            name,
            recipient_email,
            linked_user_id: linked_user_id || null,
            type_of_account,
            ...(reimbursement_user_id !== undefined && {
              reimbursement_user_id: reimbursement_user_id || null,
            }),
          },
        },
        requestOptions,
      );

      return response.data;
    } catch (error) {
      const responseErrors = error.response?.data?.errors;
      let errorMessage = "Something went wrong";

      if (error.response) {
        if (responseErrors?.user_unauthorized) {
          navigate("/feature_not_assigned", {
            state: { unavailableService: "BUAccount" },
          });
        }

        const candidates = [
          responseErrors?.bank_account_number?.[0],
          responseErrors?.beneficiary_account?.[0],
          responseErrors?.permanent_account_number?.[0],
          responseErrors?.[0]?.gstin_detail?.base?.[0],
          responseErrors?.base?.[0],
          Array.isArray(responseErrors?.category)
            ? responseErrors.category[0]
            : undefined,
          typeof error.response.data?.error === "string"
            ? error.response.data.error
            : undefined,
          error.message,
        ];

        const firstString = candidates.find(
          (msg) => typeof msg === "string" && msg.trim(),
        );
        errorMessage = firstString || "Something went wrong";
      } else if (typeof error.message === "string") {
        errorMessage = error.message;
      }

      CommonToast({
        text: errorMessage,
        icon: ICONS?.ERROR_WITH_WHITE_CIRCLE,
        variant: "var(--error)",
        buttonNeeded: false,
      });
      return rejectWithValue(responseErrors || { base: [errorMessage] });
    }
  },
);

export const beneSuccessDetail = createAsyncThunk(
  "beneSuccessDetail",
  async (id, { rejectWithValue }) => {
    try {
      if (!id) {
        throw new Error("Invalid beneficiary id");
      }
      const response = await axiosInstance.get(
        `${baseUrl}/accounts/beneficiary/${id}`,
        {
          headers: {
            "Content-Type": "application/json",
            "X-Api-Key": sessionStorage.getItem("q2p_token"),
          },
        },
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

export const beneEditDetailBank = createAsyncThunk(
  "beneEditdetailsbank",

  async (values, { rejectWithValue }) => {
    const { id, bank_account, ifsc_code, name, navigate, type } = values;

    const requestBody = {
      beneficiary_accounts: {
        name,
        bank_account_no: bank_account,
        ifsc: ifsc_code,
        ...(type === "ReimbursementBankEdit" && {
          reimbursement_account: true,
        }),
      },
    };

    try {
      const response = await axiosInstance.put(
        `${baseUrl}/accounts/beneficiary/${id}`,
        requestBody,
        {
          headers: {
            "Content-Type": "application/json",
            "X-Api-Key": sessionStorage.getItem("q2p_token"),
          },
        },
      );

      return response.data;
    } catch (error) {
      const responseData = error.response?.data;
      let errorMessage = "Something went wrong";

      if (responseData?.errors?.user_unauthorized) {
        navigate("/feature_not_assigned", {
          state: { unavailableService: "BUAccount" },
        });
        return rejectWithValue("User unauthorized");
      }

      if (responseData?.errors) {
        const errs = responseData.errors;
        errorMessage =
          errs?.category?.[0] ||
          errs?.bank_account_number?.[0] ||
          errs?.beneficiary_account?.[0] ||
          errs?.reimbursement_account?.[0] ||
          errs?.permanent_account_number?.[0] ||
          errs?.[0]?.gstin_detail?.base ||
          errs?.base?.[0] ||
          responseData?.message ||
          error.message;
      } else if (error.response) {
        errorMessage = `HTTP error! status: ${error.response.status}`;
      } else if (error.request) {
        errorMessage = "No response received from the server";
      } else {
        errorMessage = error.message;
      }
      CommonToast({
        text: errorMessage,
        icon: ICONS?.ERROR_WITH_WHITE_CIRCLE,
        variant: "var(--error)",
        buttonNeeded: false,
      });
      return rejectWithValue(errorMessage);
    }
  },
);

const beneCreateAccountSlice = createSlice({
  name: "createBeneAcc",
  initialState: {
    beneficiary: null,
    isError: false,
    beneSuccessData: null,
    isSuccessLoading: null,
    isSuccessError: null,
    isSuccessErrorData: null,
    beneEditDetailBankData: null,
    beneEditDetailBankLoading: null,
    beneEditDetailBankError: null,
    editBeneficiaryData: null,
    editBeneficiaryLoading: null,
    editBeneficiaryError: null,
    editContactBeneficiaryData: null,
    editContactBeneficiaryLoading: null,
    editContactBeneficiaryError: null,
  },
  reducers: {
    resetEditBeneficiary(state) {
      state.editBeneficiaryData = null;
    },
    resetEditBankBeneficiary(state) {
      state.beneEditDetailBankData = null;
    },
    resetCreateBene(state) {
      state.beneficiary = null;
      state.isError = null;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(editBeneficiary.pending, (state) => {
      state.editBeneficiaryLoading = true;
    });
    builder.addCase(editBeneficiary.fulfilled, (state, action) => {
      state.editBeneficiaryLoading = false;
      state.editBeneficiaryData = action.payload;
    });
    builder.addCase(editBeneficiary.rejected, (state, action) => {
      state.editBeneficiaryError = true;
    });

    builder.addCase(beneCreateAccount.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(beneCreateAccount.fulfilled, (state, action) => {
      state.isLoading = false;
      state.beneficiary = action.payload;
    });
    builder.addCase(beneCreateAccount.rejected, (state, action) => {
      state.isError = true;
    });
    builder.addCase(beneSuccessDetail.pending, (state) => {
      state.isSuccessLoading = true;
    });
    builder.addCase(beneSuccessDetail.fulfilled, (state, action) => {
      state.isSuccessErrorData = null;
      state.isSuccessLoading = false;
      state.beneSuccessData = action.payload;
    });
    builder.addCase(beneSuccessDetail.rejected, (state, action) => {
      state.isSuccessErrorData = action.payload;
      state.isSuccessError = true;
      state.beneSuccessData = null;
    });
    builder.addCase(beneEditDetailBank.pending, (state, action) => {
      state.beneEditDetailBankLoading = true;
    });
    builder.addCase(beneEditDetailBank.fulfilled, (state, action) => {
      state.beneEditDetailBankLoading = false;
      state.beneEditDetailBankData = action.payload;
    });
    builder.addCase(beneEditDetailBank.rejected, (state, action) => {
      state.beneEditDetailBankError = true;
      state.beneEditDetailBankLoading = false;
    });
  },
});

export const {
  resetEditBeneficiary,
  resetEditBankBeneficiary,
  resetCreateBene,
} = beneCreateAccountSlice.actions;

export default beneCreateAccountSlice.reducer;
